// INC2-ACCESS: Connections › Access (Andrei, 2026-10-28). Rows are agents (DEs, and your own
// personal agent, labelled), grouped by team; columns are connections (the company's ones you
// manage, and your own); each cell a level pill — No access · Read · Act (with approval or
// directly) · Manage — over the existing grants (`GET/POST access-matrix`). A hover icon edits a
// cell in a modal (level, families, limits; who changed it and when); row, column and team
// headers carry a hover icon to set many cells at once. What you may not change reads as is;
// another person's own connections never appear. A change takes effect at the agent's next tool
// call: every act is checked against its grant when it runs.
import { newIdempotencyKey, type OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  WorkerBadge,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ColumnsIcon, Grid3x3Icon, PencilIcon, RowsIcon } from "lucide-react";
import { type ReactNode, useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { RowAction } from "../settings/row-actions";
import {
  FAMILIES,
  FAMILY_LABEL,
  type Family,
  grantKeys,
  PROVIDER_NAME,
  useGrantees,
} from "./grants";

type Matrix = OperationOutput<"access.matrix">;
type Row = Matrix["rows"][number];
type Column = Matrix["columns"][number];
type Cell = Matrix["cells"][number];
type Level = "none" | "use_read" | "use_act" | "manage";
type Change = {
  connection_id: string;
  membership_id: string;
  level: Level;
  families?: Family[];
  limits?: { daily_acts?: number; recipient_domains?: string[] };
};

export const accessKey = (co: string) => ["company", co, "access-matrix"] as const;

/** A cell pill's words (Act names how its acts go out: the agent's act mode). */
function levelText(level: Level, row: Row): string {
  if (level === "use_read") return "Read";
  if (level === "use_act") return row.act_mode === "direct" ? "Act · direct" : "Act · approval";
  if (level === "manage") return "Manage";
  return "No access";
}
const LEVEL_TIP: Record<Level, string> = {
  none: "Nothing through this connection.",
  use_read: "Reads through it. Every act tool is refused.",
  use_act: "Reads and acts (sends, writes).",
  manage: "Reads, acts and grants it to others.",
};

/**
 * Whether the caller may change this cell (the server checks the same): your own connection to a
 * DE you supervise, or — no longer supervising — only to take back the grant you made.
 */
const editable = (row: Row, col: Column, cell?: Cell) =>
  col.editable &&
  row.kind === "de" &&
  (col.scope === "company" || row.supervised || cell?.grant_id != null);
/** Your own connection on a DE you no longer supervise: No access is the only change left. */
const revokeOnly = (row: Row, col: Column) => col.scope === "person" && !row.supervised;
const RANK: Record<Level, number> = { none: -1, use_read: 0, use_act: 1, manage: 2 };
/** The API takes at most this many cells in one change. */
const MAX_CHANGES = 500;

export function AccessMatrixScreen({
  api,
  co,
  agent,
}: {
  api: Api;
  co: string;
  /** One agent's row only (a DE's Connections tab). */
  agent?: string;
}) {
  const [team, setTeam] = useState("");
  const [conn, setConn] = useState("");
  const ids = { team: useId(), conn: useId() };
  const q = useQuery({
    queryKey: [...accessKey(co), team],
    queryFn: () =>
      api.client.access.matrix({
        params: { co },
        query: team ? { team } : {},
      }),
  });
  const names = useGrantees(api, co, (q.data?.cells.length ?? 0) > 0);
  const [edit, setEdit] = useState<{ row: Row; col: Column } | null>(null);
  const [bulk, setBulk] = useState<Bulk | null>(null);
  if (q.isPending) return <Loading label="Loading access" rows={4} />;
  if (q.isError) return <LoadError what="Access" error={q.error} />;
  const all = q.data;
  // The connection filter is local, so its options stay every connection
  const m = conn ? { ...all, columns: all.columns.filter((c) => c.connection_id === conn) } : all;
  const rows = agent ? m.rows.filter((r) => r.membership_id === agent) : m.rows;
  return (
    <div className="flex min-w-0 flex-col gap-3" data-slot="access-matrix">
      {agent ? null : (
        <div className="flex flex-wrap items-end gap-3">
          <Field className="w-56 max-md:w-full">
            <FieldLabel htmlFor={ids.team}>Team</FieldLabel>
            <NativeSelect id={ids.team} value={team} onChange={(e) => setTeam(e.target.value)}>
              <NativeSelectOption value="">All teams</NativeSelectOption>
              {m.teams.map((t) => (
                <NativeSelectOption key={t.id} value={t.id}>
                  {t.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <Field className="w-56 max-md:w-full">
            <FieldLabel htmlFor={ids.conn}>Connection</FieldLabel>
            <NativeSelect id={ids.conn} value={conn} onChange={(e) => setConn(e.target.value)}>
              <NativeSelectOption value="">All connections</NativeSelectOption>
              {all.columns.map((c) => (
                <NativeSelectOption key={c.connection_id} value={c.connection_id}>
                  {c.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          <span className="pb-2">
            <InfoTip label="About access">
              An agent starts with no access. A change takes effect at its next tool call: every act
              is checked against its grant when it runs. Act with approval: each act waits for a
              person; directly: within its limits.
            </InfoTip>
          </span>
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={Grid3x3Icon}
          title="No agents"
          description={team ? "No agent in this team." : "No DE or personal agent yet."}
        />
      ) : m.columns.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={Grid3x3Icon}
          title="No connections"
          description="No connection you manage, and none of your own."
        />
      ) : (
        <Grid
          m={m}
          rows={rows}
          team={team}
          nameOf={(id) => names.of({ grantee_kind: "membership", grantee_id: id }).name}
          onEdit={(row, col) => setEdit({ row, col })}
          onBulk={agent ? undefined : setBulk}
        />
      )}
      {edit ? (
        <CellDialog
          api={api}
          co={co}
          m={m}
          row={edit.row}
          col={edit.col}
          cell={cellOf(m, edit.col.connection_id, edit.row.membership_id)}
          changedBy={(id) => names.of({ grantee_kind: "membership", grantee_id: id }).name}
          onClose={() => setEdit(null)}
        />
      ) : null}
      {bulk ? (
        <BulkDialog api={api} co={co} m={m} bulk={bulk} onClose={() => setBulk(null)} />
      ) : null}
    </div>
  );
}

const cellOf = (m: Matrix, conn: string, row: string) =>
  m.cells.find((c) => c.connection_id === conn && c.membership_id === row);

type Bulk =
  | { kind: "row"; row: Row }
  | { kind: "column"; col: Column }
  | { kind: "team"; teamId: string | null; label: string; rows: Row[] };

/** The grid: sticky header row and first column; the grid scrolls, the page does not. */
function Grid({
  m,
  rows,
  team,
  nameOf,
  onEdit,
  onBulk,
}: {
  m: Matrix;
  rows: Row[];
  /** The team filtered on: its members sit under it. */
  team: string;
  nameOf: (membershipId: string) => string;
  onEdit: (row: Row, col: Column) => void;
  onBulk: ((b: Bulk) => void) | undefined;
}) {
  // Grouped by team (an agent in several teams sits under the filtered one, else its first); your
  // agent last
  const groups = useMemo(() => {
    const teamName = new Map(m.teams.map((t) => [t.id, t.name]));
    const out = new Map<string, { teamId: string | null; label: string; rows: Row[] }>();
    for (const r of rows) {
      const t =
        r.kind === "ag"
          ? "ag"
          : team && r.team_ids.includes(team)
            ? team
            : (r.team_ids[0] ?? "none");
      const label =
        t === "ag" ? "Your agent" : t === "none" ? "No team" : (teamName.get(t) ?? "A team");
      const g = out.get(t) ?? { teamId: t === "ag" || t === "none" ? null : t, label, rows: [] };
      g.rows.push(r);
      out.set(t, g);
    }
    return [...out.entries()]
      .sort(([a], [b]) =>
        a === "ag" ? 1 : b === "ag" ? -1 : a === "none" ? 1 : b === "none" ? -1 : 0,
      )
      .map(([, g]) => g);
  }, [rows, team, m.teams]);
  return (
    <div
      className="max-h-[70vh] min-w-0 overflow-auto rounded-lg border border-border"
      data-slot="access-grid"
    >
      <table className="border-separate border-spacing-0 text-[13px]">
        <thead>
          <tr>
            <th
              scope="col"
              className="sticky top-0 left-0 z-20 h-9 min-w-56 border-b border-r border-border bg-card px-3 text-left font-medium"
            >
              Agent
            </th>
            {m.columns.map((c) => (
              <th
                key={c.connection_id}
                scope="col"
                data-connection={c.connection_id}
                className="group/cell sticky top-0 z-10 h-9 min-w-36 border-b border-border bg-card px-2 text-left font-medium"
              >
                <span className="flex items-center gap-1">
                  <span className="truncate" title={c.identity_display ?? c.label}>
                    {c.label}
                  </span>
                  <Badge variant="outline">{PROVIDER_NAME[c.provider] ?? c.provider}</Badge>
                  {c.scope === "person" ? <Badge variant="neutral">Yours</Badge> : null}
                  {onBulk && c.editable ? (
                    <CellHover>
                      <RowAction
                        icon={ColumnsIcon}
                        label={`Set ${c.label} for every agent`}
                        tip="Set column"
                        onClick={() => onBulk({ kind: "column", col: c })}
                      />
                    </CellHover>
                  ) : null}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.label} data-group={g.teamId ?? g.label}>
            <tr>
              <th
                scope="rowgroup"
                colSpan={m.columns.length + 1}
                className="group/cell sticky left-0 h-8 border-b border-border bg-muted/50 px-3 text-left text-xs font-medium text-muted-foreground"
              >
                <span className="flex items-center gap-1">
                  {g.label}
                  {onBulk && g.rows.some((r) => r.kind === "de") ? (
                    <CellHover>
                      <RowAction
                        icon={Grid3x3Icon}
                        label={`Set access for ${g.label}`}
                        tip="Set for the group"
                        onClick={() =>
                          onBulk({
                            kind: "team",
                            teamId: g.teamId,
                            label: g.label,
                            // Every member of the team, also those shown under another team
                            rows: g.teamId
                              ? m.rows.filter(
                                  (r) => r.kind === "de" && r.team_ids.includes(g.teamId as string),
                                )
                              : g.rows,
                          })
                        }
                      />
                    </CellHover>
                  ) : null}
                </span>
              </th>
            </tr>
            {g.rows.map((r) => (
              <tr key={r.membership_id} data-agent={r.membership_id}>
                <th
                  scope="row"
                  className="group/cell sticky left-0 z-10 h-9 border-b border-r border-border bg-card px-3 text-left font-normal"
                >
                  <span className="flex items-center gap-1.5">
                    <WorkerBadge kind={r.kind} size="sm" />
                    <span className="truncate">{r.name}</span>
                    {r.seat_title ? (
                      <span className="truncate text-xs text-muted-foreground">{r.seat_title}</span>
                    ) : null}
                    {onBulk && m.columns.some((c) => editable(r, c) && !revokeOnly(r, c)) ? (
                      <CellHover>
                        <RowAction
                          icon={RowsIcon}
                          label={`Set every connection for ${r.name}`}
                          tip="Set row"
                          onClick={() => onBulk({ kind: "row", row: r })}
                        />
                      </CellHover>
                    ) : null}
                  </span>
                </th>
                {m.columns.map((c) => {
                  const cell = cellOf(m, c.connection_id, r.membership_id);
                  const can = editable(r, c, cell);
                  return (
                    <td
                      key={c.connection_id}
                      data-cell={`${r.membership_id}:${c.connection_id}`}
                      data-level={cell?.level ?? "none"}
                      className="group/cell h-9 border-b border-border px-2"
                    >
                      <span className="flex items-center gap-1">
                        <LevelPill row={r} cell={cell} nameOf={nameOf} />
                        {can ? (
                          <CellHover>
                            <RowAction
                              icon={PencilIcon}
                              label={`Edit ${r.name} on ${c.label}`}
                              tip="Edit"
                              onClick={() => onEdit(r, c)}
                            />
                          </CellHover>
                        ) : null}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}

function CellHover({ children }: { children: ReactNode }) {
  return (
    <span className="ml-auto opacity-0 group-hover/cell:opacity-100 group-focus-within/cell:opacity-100 pointer-coarse:opacity-100">
      {children}
    </span>
  );
}

/** A small level pill; its tooltip holds where it comes from, families, limits, who and when. */
function LevelPill({
  row,
  cell,
  nameOf,
}: {
  row: Row;
  cell: Cell | undefined;
  nameOf: (membershipId: string) => string;
}) {
  const level: Level = cell?.level ?? "none";
  const pill = (
    <Badge
      variant={level === "none" ? "outline" : level === "manage" ? "accent" : "neutral"}
      className={cn(level === "none" && "text-muted-foreground")}
      data-slot="level-pill"
    >
      {levelText(level, row)}
      {cell && cell.via !== "direct" ? ` · via ${cell.via}` : ""}
    </Badge>
  );
  if (!cell) return pill;
  return (
    <Tooltip>
      <TooltipTrigger render={<button type="button" className="rounded-full" />}>
        {pill}
      </TooltipTrigger>
      <TooltipContent>
        <span className="flex flex-col gap-0.5">
          <span>{LEVEL_TIP[level]}</span>
          {/* Every grant it acts under: an act may use any of them */}
          {cell.sources.map((s) => (
            <span key={`${s.via}-${s.level}-${s.families.join()}`}>
              {s.via === "direct" ? "Own" : `Via ${s.via}`}: {levelText(s.level, row)} ·{" "}
              {s.families.length
                ? s.families.map((f) => FAMILY_LABEL[f]).join(", ")
                : "All families"}
              {s.limits.daily_acts ? ` · ${s.limits.daily_acts} acts a day` : ""}
              {s.limits.recipient_domains?.length
                ? ` · to ${s.limits.recipient_domains.join(", ")}`
                : ""}
            </span>
          ))}
          <span>
            {cell.changed_by_membership_id ? `${nameOf(cell.changed_by_membership_id)} · ` : ""}
            {formatDateTime(cell.changed_at)}
          </span>
        </span>
      </TooltipContent>
    </Tooltip>
  );
}

/** Level, families and limits fields (one cell, or many at once). */
function LevelFields({
  personal,
  level,
  setLevel,
  families,
  setFamilies,
  daily,
  setDaily,
  domains,
  setDomains,
  directAvailable,
  direct,
  setDirect,
  revoke = false,
  min = null,
}: {
  personal: boolean;
  /** Only No access may be chosen (your own connection on a DE you no longer supervise). */
  revoke?: boolean;
  /** The level its seat or a team gives: nothing lower can be set here. */
  min?: Level | null;
  level: Level;
  setLevel: (l: Level) => void;
  families: Family[];
  setFamilies: (f: Family[]) => void;
  daily: string;
  setDaily: (v: string) => void;
  domains: string;
  setDomains: (v: string) => void;
  directAvailable: boolean;
  direct: boolean;
  setDirect: (d: boolean) => void;
}) {
  const name = useId();
  const ids = { daily: useId(), domains: useId() };
  const options: { value: Level; label: string; direct?: boolean }[] = revoke
    ? [{ value: "none", label: "No access" }]
    : [
        { value: "none", label: "No access" },
        { value: "use_read", label: "Read" },
        { value: "use_act", label: "Act with approval" },
        { value: "use_act", label: "Act directly", direct: true },
        ...(personal ? [] : [{ value: "manage" as const, label: "Manage" }]),
      ];
  return (
    <>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium">Level</legend>
        {options.map((o) => {
          const id = `${name}-${o.label}`;
          const below = min !== null && RANK[o.value] < RANK[min];
          const off = (o.direct === true && !directAvailable) || below;
          const checked = level === o.value && (o.value !== "use_act" || direct === !!o.direct);
          return (
            <span key={o.label} className="flex items-center gap-2">
              <input
                type="radio"
                id={id}
                name={name}
                checked={checked}
                disabled={off}
                onChange={() => {
                  setLevel(o.value);
                  setDirect(!!o.direct);
                }}
              />
              <Label htmlFor={id} className={cn(off && "text-muted-foreground")}>
                {o.label}
              </Label>
              {o.direct && !directAvailable ? (
                <InfoTip label="About acting directly">
                  Every outward act is approved first for now. Acting directly opens once outward
                  acts are held by the agent policy on every path.
                </InfoTip>
              ) : null}
            </span>
          );
        })}
      </fieldset>
      {min ? (
        <p className="text-xs text-muted-foreground">
          Its seat or a team gives{" "}
          {min === "use_read" ? "Read" : min === "use_act" ? "Act" : "Manage"}; change it there to
          go lower.
        </p>
      ) : null}
      {level !== "none" ? (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 flex items-center gap-1 text-sm font-medium">
            Families
            <InfoTip label="About families">
              None ticked: every family the connection serves.
              {personal ? " Your own connection: name them to let a DE act." : ""}
            </InfoTip>
          </legend>
          <div className="flex flex-wrap gap-3">
            {FAMILIES.map((f) => (
              <Label key={f} className="flex items-center gap-1.5 font-normal">
                <Checkbox
                  checked={families.includes(f)}
                  onCheckedChange={(on) =>
                    setFamilies(on ? [...families, f] : families.filter((x) => x !== f))
                  }
                />
                {FAMILY_LABEL[f]}
              </Label>
            ))}
          </div>
        </fieldset>
      ) : null}
      {level === "use_act" || level === "manage" ? (
        <div className="grid grid-cols-2 gap-3 max-md:grid-cols-1">
          <Field>
            <FieldLabel htmlFor={ids.daily}>Acts a day</FieldLabel>
            <Input
              id={ids.daily}
              inputMode="numeric"
              placeholder="No limit"
              value={daily}
              onChange={(e) => setDaily(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.domains}>Recipient domains</FieldLabel>
            <Input
              id={ids.domains}
              placeholder="Any"
              value={domains}
              onChange={(e) => setDomains(e.target.value)}
            />
          </Field>
        </div>
      ) : null}
    </>
  );
}

/** The form state shared by the cell and bulk dialogs, and the change it makes. */
function useLevelForm(initial?: Cell["sources"][number], row?: Row, start: Level = "use_read") {
  const [level, setLevel] = useState<Level>(initial?.level ?? start);
  const [direct, setDirect] = useState(row?.act_mode === "direct");
  const [families, setFamilies] = useState<Family[]>((initial?.families as Family[]) ?? []);
  const [daily, setDaily] = useState(initial?.limits.daily_acts?.toString() ?? "");
  const [domains, setDomains] = useState(initial?.limits.recipient_domains?.join(", ") ?? "");
  const dailyN = daily.trim() ? Number(daily) : null;
  const domainList = domains
    .split(/[\s,]+/)
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
  const badDaily = dailyN !== null && (!Number.isInteger(dailyN) || dailyN < 1 || dailyN > 10_000);
  const change = (connection_id: string, membership_id: string): Change => ({
    connection_id,
    membership_id,
    level,
    ...(level !== "none" && families.length ? { families } : {}),
    ...((level === "use_act" || level === "manage") && (dailyN !== null || domainList.length)
      ? {
          limits: {
            ...(dailyN !== null ? { daily_acts: dailyN } : {}),
            ...(domainList.length ? { recipient_domains: domainList } : {}),
          },
        }
      : {}),
  });
  return {
    fields: {
      level,
      setLevel,
      families,
      setFamilies,
      daily,
      setDaily,
      domains,
      setDomains,
      direct,
      setDirect,
    },
    badDaily,
    change,
    actMode: level === "use_act" ? (direct ? ("direct" as const) : undefined) : undefined,
  };
}

function useApply(api: Api, co: string, done: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { changes: Change[]; act_mode?: "direct" | "approval" }) =>
      api.client.access.apply({ params: { co }, idempotencyKey: newIdempotencyKey(), body }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: accessKey(co) });
      void qc.invalidateQueries({ queryKey: grantKeys.all(co) });
      toast.add({
        title: r.changed ? "Access updated" : "Nothing changed",
        type: "success",
      });
      done();
    },
    onError: (e) => toast.add({ title: problemText(e, "Access not changed."), type: "error" }),
  });
}

function CellDialog({
  api,
  co,
  m,
  row,
  col,
  cell,
  changedBy,
  onClose,
}: {
  api: Api;
  co: string;
  m: Matrix;
  row: Row;
  col: Column;
  cell: Cell | undefined;
  changedBy: (membershipId: string) => string;
  onClose: () => void;
}) {
  const revoke = revokeOnly(row, col);
  const own = revoke ? undefined : cell?.sources.find((s) => s.via === "direct");
  const min = cell?.inherited_level ?? null;
  const f = useLevelForm(own, row, revoke ? "none" : (min ?? "use_read"));
  const apply = useApply(api, co, onClose);
  const personal = col.scope === "person";
  const needsFamilies = personal && f.fields.level === "use_act" && f.fields.families.length === 0;
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent data-slot="access-cell-dialog">
        <DialogHeader>
          <DialogTitle>
            {row.name} · {col.label}
          </DialogTitle>
          <DialogDescription>
            {cell
              ? `${cell.via === "direct" ? "Set" : `Via its ${cell.via}`} by ${
                  cell.changed_by_membership_id ? changedBy(cell.changed_by_membership_id) : "—"
                }, ${formatDateTime(cell.changed_at)}`
              : "No access yet."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          aria-label="Edit access"
          onSubmit={(e) => {
            e.preventDefault();
            if (f.badDaily || needsFamilies) return;
            apply.mutate({
              changes: [f.change(col.connection_id, row.membership_id)],
              ...(f.actMode ? { act_mode: f.actMode } : {}),
            });
          }}
        >
          <LevelFields
            personal={personal}
            revoke={revoke}
            min={min}
            directAvailable={m.direct_available}
            {...f.fields}
          />
          {revoke ? (
            <p className="text-xs text-muted-foreground">
              You no longer supervise this DE: you can only take your connection back.
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={apply.isPending || f.badDaily || needsFamilies}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Many cells at once: a row (every editable connection), a column (every editable agent), a team. */
function BulkDialog({
  api,
  co,
  m,
  bulk,
  onClose,
}: {
  api: Api;
  co: string;
  m: Matrix;
  bulk: Bulk;
  onClose: () => void;
}) {
  const f = useLevelForm();
  const apply = useApply(api, co, onClose);
  const connId = useId();
  const [teamConn, setTeamConn] = useState(m.columns.find((c) => c.editable)?.connection_id ?? "");
  const pairs: { row: Row; col: Column }[] = (
    bulk.kind === "row"
      ? m.columns.map((col) => ({ row: bulk.row, col }))
      : bulk.kind === "column"
        ? m.rows.map((row) => ({ row, col: bulk.col }))
        : bulk.rows
            .map((row) => ({ row, col: m.columns.find((c) => c.connection_id === teamConn) }))
            .filter((t): t is { row: Row; col: Column } => !!t.col)
  ).filter((t) => editable(t.row, t.col) && !revokeOnly(t.row, t.col));
  // A cell whose seat or team gives more than the chosen level keeps it (set it there)
  const held = (t: { row: Row; col: Column }) =>
    cellOf(m, t.col.connection_id, t.row.membership_id)?.inherited_level ?? null;
  const targets = pairs.filter((t) => {
    const h = held(t);
    return h === null || RANK[h] <= RANK[f.fields.level];
  });
  const kept = pairs.length - targets.length;
  const tooMany = targets.length > MAX_CHANGES;
  const personal = targets.some((t) => t.col.scope === "person");
  const needsFamilies = personal && f.fields.level === "use_act" && f.fields.families.length === 0;
  const title =
    bulk.kind === "row"
      ? `${bulk.row.name}: every connection`
      : bulk.kind === "column"
        ? `${bulk.col.label}: every agent`
        : `${bulk.label}: one connection`;
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent data-slot="access-bulk-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {targets.length === 1 ? "1 cell" : `${targets.length} cells`} you may change.
            {kept ? ` ${kept} keep a higher level from their seat or team.` : ""}
            {tooMany ? ` At most ${MAX_CHANGES} at once: filter by team first.` : ""}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          aria-label="Set access"
          onSubmit={(e) => {
            e.preventDefault();
            if (!targets.length || tooMany || f.badDaily || needsFamilies) return;
            apply.mutate({
              changes: targets.map((t) => f.change(t.col.connection_id, t.row.membership_id)),
              ...(f.actMode ? { act_mode: f.actMode } : {}),
            });
          }}
        >
          {bulk.kind === "team" ? (
            <Field>
              <FieldLabel htmlFor={connId}>Connection</FieldLabel>
              <NativeSelect
                id={connId}
                value={teamConn}
                onChange={(e) => setTeamConn(e.target.value)}
              >
                {m.columns
                  .filter((c) => c.editable)
                  .map((c) => (
                    <NativeSelectOption key={c.connection_id} value={c.connection_id}>
                      {c.label}
                    </NativeSelectOption>
                  ))}
              </NativeSelect>
            </Field>
          ) : null}
          <LevelFields personal={personal} directAvailable={m.direct_available} {...f.fields} />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                apply.isPending || !targets.length || tooMany || f.badDaily || needsFamilies
              }
            >
              Apply
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What the DE's template asks for (`tool_grants.connections`), matched to the company's
 * connections, for confirmation: nothing is granted until you tick and apply. A hire starts with
 * no access; this is where the template's levels are prefilled.
 */
export function TemplateAccess({ api, co, de }: { api: Api; co: string; de: string }) {
  const q = useQuery({
    queryKey: [...accessKey(co), "template", de],
    queryFn: () => api.client.access.template({ params: { co, id: de } }),
  });
  const [open, setOpen] = useState(false);
  const open_ = (q.data?.proposals ?? []).filter((p) => !p.satisfied);
  if (!q.data || open_.length === 0) return null;
  return (
    <>
      <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>
        Template access ({open_.length})
      </Button>
      {open ? (
        <TemplateDialog
          api={api}
          co={co}
          de={de}
          proposals={q.data.proposals}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function TemplateDialog({
  api,
  co,
  de,
  proposals,
  onClose,
}: {
  api: Api;
  co: string;
  de: string;
  proposals: OperationOutput<"access.template">["proposals"];
  onClose: () => void;
}) {
  const ready = proposals.filter((p) => !p.satisfied && p.connection_id);
  const [picked, setPicked] = useState<Set<number>>(() => new Set(ready.map((_, i) => i)));
  const apply = useApply(api, co, onClose);
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent data-slot="access-template-dialog">
        <DialogHeader>
          <DialogTitle>Template access</DialogTitle>
          <DialogDescription>
            What this DE's template needs. Confirm what to grant.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          aria-label="Template access"
          onSubmit={(e) => {
            e.preventDefault();
            const changes = ready
              .filter((_, i) => picked.has(i))
              .map((p) => ({
                connection_id: p.connection_id as string,
                membership_id: de,
                level: p.level as Level,
                ...(p.families.length ? { families: p.families as Family[] } : {}),
              }));
            if (changes.length) apply.mutate({ changes });
          }}
        >
          <ul className="flex flex-col gap-2">
            {proposals.map((p) => {
              const i = ready.indexOf(p);
              const label = `${PROVIDER_NAME[p.provider] ?? p.provider}: ${
                p.level === "use_act" ? "Act" : "Read"
              }`;
              return (
                <li
                  key={`${p.provider}-${p.level}-${p.families.join()}`}
                  className="flex items-start gap-2 text-sm"
                >
                  {i >= 0 ? (
                    <Checkbox
                      aria-label={label}
                      checked={picked.has(i)}
                      onCheckedChange={(on) => {
                        const next = new Set(picked);
                        if (on) next.add(i);
                        else next.delete(i);
                        setPicked(next);
                      }}
                    />
                  ) : (
                    <span className="size-4" />
                  )}
                  <span className="flex min-w-0 flex-col">
                    <span>
                      {label}
                      {p.families.length
                        ? ` · ${p.families.map((f) => FAMILY_LABEL[f as Family]).join(", ")}`
                        : ""}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {p.satisfied
                        ? "Already granted"
                        : p.connection_id
                          ? `Through ${p.connection_label}`
                          : "Not connected in this company"}
                      {p.why ? ` · ${p.why}` : ""}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={apply.isPending || picked.size === 0}>
              Grant
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
